import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRulesetAppData } from '../store/appData';
import { RESET_RULE_SET, UPDATE_RULE_SET } from '../store/ruleSet';
import { AdminSidePanelAction, getRuleSetTypeLabel, getRequestTypeLabel, getRequestSubTypeLabel, getRuleSetActiveLabel } from '../../Enums';
import { addSearchIndex, removeSearchIndex } from '../../../common-client2/src/services/searchIndexer';
import RuleSetHeader from './RuleSetHeader';
import RuleSetTable from './RuleSetTable';
import RuleSetEdit from './RuleSetEdit';
import { getHyperfindPriorityLabel } from "../RuleSet/RuleSetEdit/Step2-SortingRuleSection/HyperfindParameter.service";
import { AwardingRuleParameterType, getAwardingRuleParameterTypeLabel, getOrderLabel, DataType } from '../../Enums';
import './RuleSet.scss';

/**
 * 
 * @param {ApiAdmin.IRuleSetBriefPresentationModel} ruleSet 
 * @returns {RuleSet.IRuleSetBrief}
 */
const mapRuleSetToTable = ((ruleSet) => ({
    ...ruleSet,
    ruleSetTypeName: getRuleSetTypeLabel(ruleSet.ruleSetType),
    requestTypeName: ruleSet.requestType !== null ? getRequestTypeLabel(ruleSet.requestType) : null,
    requestSubtypeName: ruleSet.requestSubType !== null ? getRequestSubTypeLabel(ruleSet.requestType, ruleSet.requestSubtype) : null,
    activeLabel: getRuleSetActiveLabel(ruleSet.isActive),
    actionButtons: {
        isEditDisplayed: !ruleSet.isCustomRule,
        isDeleteDisabled: !ruleSet.isEditable,
    },
}));
function RuleSet() {
    const dispatch = useDispatch();
    /** @type {EmploymentBillboard.AdminStore} */
    // @ts-ignore
    const { adminAppData } = useSelector((state) => state);
    const { ruleSets, isAdminRuleSetAppDataLoaded, hyperfinds, customDataFields, dataviews, customDateFormat } = adminAppData;

    /**
     * @type {[boolean, Function]}
     */
    const [showSidePanel, setShowSidePanel] = React.useState(false);

    /**
     * @type {[string, Function]}
     */
    const [searchValue, setSearchValue] = React.useState('');

    const indexedData = React.useMemo(() => ruleSets.map(x => {
        const tableModel = mapRuleSetToTable(x);
        return addSearchIndex(tableModel);
    }), [JSON.stringify(ruleSets)]);

    let filteredData = !searchValue
        ? indexedData
        : indexedData.filter((i) => i.searchIndex?.includes(searchValue.toLocaleLowerCase()));

    const cleanedUpFilteredData = filteredData.map(removeSearchIndex);

    const handleCreateNewRuleSet = () => {
        dispatch({
            type: RESET_RULE_SET,
        });
        dispatch({
            type: UPDATE_RULE_SET,
            payload: {
                adminSidePanelAction: AdminSidePanelAction.Create,
                isEditable: true,
            }
        });
        setShowSidePanel(true);
    };

    React.useEffect(() => {
        if (!isAdminRuleSetAppDataLoaded) {
            dispatch(fetchRulesetAppData());
        }
    }, []);

    
    const getCustomDateFormatLabel = (dataType, customDateFormatId) => {
        if (dataType === DataType.Numeric) {
            return 'Numeric';
        }
        const selectedFormat = customDateFormat.find((x) => x.id === customDateFormatId);
        if (selectedFormat) {
            if (selectedFormat.dataType === DataType.Date) {
                return `Date ${selectedFormat.name}`;
            }
            if (selectedFormat.dataType === DataType.Time) {
                return `Time ${selectedFormat.name}`;
            }
        }
        return '';
    }
    const updatedFilteredData = cleanedUpFilteredData.map((item) => {
        if (item.awardingRules && Array.isArray(item.awardingRules)) {
            item.awardingRules = item.awardingRules.map((rule) => {
                switch (rule.type) {
                    case AwardingRuleParameterType.CustomDataType:
                        const customDataField = customDataFields.find(c => c.id === rule.customDataFieldInfos.customDataFieldId);
                        if (customDataField) {
                            rule.customDataFieldInfos.name = customDataField.name;
                        }
                        rule.customDataFieldInfos.dateFormatLabel = getCustomDateFormatLabel(rule.customDataFieldInfos.dataType, rule.customDataFieldInfos.customDateFormat);
                        rule.orderLabel = getOrderLabel(rule.order);
                        rule.awardingRuleParameterTypeLabel = getAwardingRuleParameterTypeLabel(rule.type);
                        break;
    
                    case AwardingRuleParameterType.HyperfindBelonging:
                        const matchingHyperfind = hyperfinds.find(h => h.value === rule.hyperfindBelongingInfos.hyperfindId);
                        if (matchingHyperfind) {
                            rule.hyperfindBelongingInfos.label = matchingHyperfind.label;
                        }
                        rule.awardingRuleParameterTypeLabel = getAwardingRuleParameterTypeLabel(rule.type);
                        rule.hyperfindBelongingInfos.priorityLabel = getHyperfindPriorityLabel(rule.order);
                        break;
    
                    case AwardingRuleParameterType.DataviewData:
                        const dataview = dataviews.find(d => d.id === rule.dataviewInfos.dataviewId);
                        if (dataview) {
                            rule.dataviewInfos.dataviewName = dataview.name;
                            const dataviewField = dataview.fields.find(f => f.key === rule.dataviewInfos.dataviewKeyAlias);
                            if (dataviewField) {
                                rule.dataviewInfos.dataviewFieldLabel = dataviewField.label;
                            }
                        }
                        rule.dataviewInfos.dateFormatLabel = getCustomDateFormatLabel(rule.dataviewInfos.dataType, rule.dataviewInfos.customDateFormat);
                        rule.orderLabel = getOrderLabel(rule.order);
                        rule.awardingRuleParameterTypeLabel = getAwardingRuleParameterTypeLabel(rule.type);
                        break;
    
                    case AwardingRuleParameterType.BySubmittedRequestDateTime:
                        rule.awardingRuleParameterTypeLabel = getAwardingRuleParameterTypeLabel(rule.type);
                        rule.orderLabel = getOrderLabel(rule.order);
                        break;
                }
    
                return rule;
            });
        }
        return item;
    });
    
    return (
        <>
            <RuleSetHeader
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onCreate={handleCreateNewRuleSet}
            />
            <RuleSetTable
                data={updatedFilteredData}
                setShowSidePanel={setShowSidePanel}
            />
            <RuleSetEdit
                open={showSidePanel}
                setOpen={setShowSidePanel}
            />
        </>
    );
}
export default RuleSet;
